import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from '../utils/axiosConfig';
import { getActionErrorMessage } from '../utils/errorHandler';
import clinExpertLogo from "../ClinExpert_logo.png";
import "../styles/LoginForm.css";
import { FaLock, FaEye, FaEyeSlash, FaCheck, FaTimes, FaShieldAlt, FaArrowLeft } from "react-icons/fa";
import AuthBackButton from "./AuthBackButton";

const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  const validatePassword = (password) => {
    const strength = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordStrength(strength);
    return Object.values(strength).every(Boolean);
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    validatePassword(password);
    setError("");
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setError("");
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!validatePassword(newPassword)) {
      setError("Password does not meet security requirements.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosInstance.post("/auth/reset-password", {
        token: token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      if (response.data.status === "success") {
        setSuccess("Password reset successfully! Redirecting to login...");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (err) {
      console.error(err);
      const detail = String(err?.response?.data?.detail || "").toLowerCase();

      if (detail.includes("reset token")) {
        setError("This reset link is invalid or has expired. Please request a new password reset link.");
      } else if (detail.includes("password must")) {
        setError("Your new password does not meet the security requirements.");
      } else if (detail.includes("do not match")) {
        setError("Passwords do not match.");
      } else {
        // Use sanitized fallback message - never show raw backend errors
        setError(getActionErrorMessage(err, "reset password"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderPasswordStrengthItem = (isValid, label) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
      {isValid ? (
        <FaCheck style={{ color: '#10b981', flexShrink: 0 }} />
      ) : (
        <FaTimes style={{ color: '#dc2626', flexShrink: 0 }} />
      )}
      <span style={{ color: isValid ? '#10b981' : '#6b7280' }}>{label}</span>
    </div>
  );

  if (!token) {
    return (
      <div className="login-container">
        <AuthBackButton />
        <div className="login-shell forgot-shell">
          <section className="login-brand-panel forgot-brand-panel">
            <img src={clinExpertLogo} alt="ClinExpert Logo" className="logo dual-logo" />
            <h1 className="login-title">ClinExpert Portal</h1>
            <p className="login-subtitle">Secure password recovery for your institutional account.</p>
            <div className="login-feature-list">
              <div className="login-feature-item">
                <FaShieldAlt />
                <span>Reset access with a secure link</span>
              </div>
              <div className="login-feature-item">
                <FaCheck />
                <span>Clean, familiar login experience</span>
              </div>
            </div>
            <p className="login-brand-footer">Brought to you by ClinExpert Inc</p>
          </section>

          <section className="login-form-panel forgot-form-panel content-box">
            <div className="forgot-header">
              <h1 className="forgot-title">Invalid Reset Link</h1>
              <p className="forgot-subtitle">The password reset link is invalid or has expired. Request a new link to continue.</p>
            </div>

            <div className="login-inline-status" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FaTimes style={{ flexShrink: 0 }} />
              <span>Please request a new reset link from the forgot password page.</span>
            </div>

            <div className="forgot-footer-actions" style={{ marginTop: '1.5rem' }}>
              <button
                type="button"
                className="forgot-back-link"
                onClick={() => navigate("/forgot-password")}
              >
                <FaArrowLeft />
                Request New Reset Link
              </button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <AuthBackButton />
      <div className="login-shell forgot-shell">
        <section className="login-brand-panel forgot-brand-panel">
          <img src={clinExpertLogo} alt="ClinExpert Logo" className="logo dual-logo" />
          <h1 className="login-title">ClinExpert Portal</h1>
          <p className="login-subtitle">Secure password recovery for your institutional account.</p>
          <div className="login-feature-list">
            <div className="login-feature-item">
              <FaShieldAlt />
              <span>Reset access with a secure link</span>
            </div>
            <div className="login-feature-item">
              <FaCheck />
              <span>Clean, familiar login experience</span>
            </div>
          </div>
          <p className="login-brand-footer">Brought to you by ClinExpert Inc</p>
        </section>

        <section className="login-form-panel forgot-form-panel content-box">
          <div className="forgot-header">
            <h1 className="forgot-title">Reset Password</h1>
            <p className="forgot-subtitle">Enter a new password that meets the security requirements below.</p>
          </div>

          <form onSubmit={handleResetPassword}>
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">New Password</label>
              <div className="input-group">
                <span className="input-icon">
                  <FaLock />
                </span>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="input-icon input-eye"
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Confirm New Password</label>
              <div className="input-group">
                <span className="input-icon">
                  <FaLock />
                </span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="input-icon input-eye"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.875rem', color: '#5f7993', marginBottom: '0.65rem', fontWeight: 600 }}>
                Password Requirements:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.55rem', fontSize: '0.78rem', lineHeight: 1.35 }}>
                {renderPasswordStrengthItem(passwordStrength.length, 'At least 8 characters')}
                {renderPasswordStrengthItem(passwordStrength.uppercase, 'One uppercase letter')}
                {renderPasswordStrengthItem(passwordStrength.lowercase, 'One lowercase letter')}
                {renderPasswordStrengthItem(passwordStrength.number, 'One number')}
                {renderPasswordStrengthItem(passwordStrength.special, 'One special character')}
              </div>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={
                isLoading ||
                !Object.values(passwordStrength).every(Boolean) ||
                newPassword !== confirmPassword
              }
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>

            {error && <div className="login-inline-status" role="status">{error}</div>}
            {success && <div className="forgot-success-pill" style={{ marginTop: '1rem' }}>{success}</div>}
          </form>
        </section>
      </div>
    </div>
  );
};

export default ResetPasswordForm; 